import { useId } from 'react';
import { cn } from '../lib/cn';

interface SparklineProps {
  data: number[];
  width?: number;
  height?: number;
  /** Цвет линии; по умолчанию — по направлению (рост зелёный / падение красный). */
  color?: string;
  /** Мягкая заливка под линией. */
  fill?: boolean;
  className?: string;
  'aria-label'?: string;
}

/** Кастомный SVG-спарклайн — без графических библиотек (ТЗ §1). */
export function Sparkline({ data, width = 96, height = 28, color, fill = true, className, ...rest }: SparklineProps) {
  const rawId = useId();
  const gid = `ops-spark-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  if (!data || data.length < 2) {
    return <svg width={width} height={height} className={className} aria-hidden="true" />;
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const pad = 2;
  const innerH = height - pad * 2;
  const stepX = width / (data.length - 1);

  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = pad + innerH - ((v - min) / span) * innerH;
    return [x, y] as const;
  });

  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${width.toFixed(1)},${height.toFixed(1)} L0,${height.toFixed(1)} Z`;
  const stroke = color ?? (data[data.length - 1] >= data[0] ? 'var(--ops-success)' : 'var(--ops-danger)');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('overflow-visible', className)}
      role="img"
      {...rest}
    >
      {fill && (
        <>
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
              <stop offset="100%" stopColor={stroke} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={area} fill={`url(#${gid})`} />
        </>
      )}
      <path d={line} fill="none" stroke={stroke} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
